// crewbit-admin/src/components/money/BusinessSwitcherHeader.tsx
//
// Persistent business switcher for the admin sidebar logo area.
// Spec: docs/references/MONEY_MODULE_WIRING_v1.md §7.1.
//
// Branches:
// - Single-membership users see a static label (no chevron, not clickable).
// - Multi-membership users see "{businessName} ▾" — click opens the popover.
// - requiresSelection state surfaces "Pick a business →" prompt.
// - Zero-membership state renders nothing (admin AuthGate already blocks
//   non-business users from this surface, so this branch is defensive).
//
// Sized for the admin sidebar's existing "Business selector" slot. Inherits
// the dark sidebar palette from styles/tokens.ts `sidebar`.

import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Building2 } from 'lucide-react';
import { sidebar as sb, transition } from '../../styles/tokens';
import { useActiveBusiness } from '../../hooks/useActiveBusiness';
import BusinessPickerPopover from './BusinessPickerPopover';

interface Props {
  /** Render the icon-only collapsed variant when the sidebar is collapsed. */
  collapsed?: boolean;
}

export default function BusinessSwitcherHeader({ collapsed = false }: Props) {
  const {
    activeBusinessId,
    activeBusinessName,
    requiresSelection,
    memberships,
    setActiveBusiness,
  } = useActiveBusiness();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const handleSelect = useCallback(
    async (businessId: string) => {
      close();
      if (businessId === activeBusinessId) return;
      await setActiveBusiness(businessId);
    },
    [activeBusinessId, setActiveBusiness, close],
  );

  if (memberships.length === 0) return null;

  const isClickable = memberships.length > 1;
  const labelText = requiresSelection
    ? 'Pick a business'
    : activeBusinessName ?? 'Business';

  // Collapsed sidebar: icon-only chip — no popover (sidebar must be expanded
  // to switch). Keeps the header element visually present without overflowing
  // the 64px collapsed width.
  if (collapsed) {
    return (
      <div
        title={isClickable ? `Active business: ${labelText} (expand sidebar to switch)` : labelText}
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: 'rgba(108,180,238,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: sb.accentBar,
          margin: '8px auto 0',
        }}
      >
        <Building2 size={16} strokeWidth={1.75} />
      </div>
    );
  }

  return (
    <div ref={triggerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => isClickable && setOpen((v) => !v)}
        disabled={!isClickable}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '10px 12px',
          borderRadius: 8,
          background: requiresSelection
            ? 'rgba(108,180,238,0.18)'
            : sb.surfaceSubtle,
          border: `1px solid ${requiresSelection ? 'rgba(108,180,238,0.45)' : sb.borderSubtle}`,
          cursor: isClickable ? 'pointer' : 'default',
          fontFamily: 'inherit',
          textAlign: 'left',
          color: 'inherit',
          transition: `all ${transition.fast}`,
        }}
        onMouseEnter={(e) => {
          if (!isClickable) return;
          e.currentTarget.style.background = sb.bgHover;
        }}
        onMouseLeave={(e) => {
          if (!isClickable) return;
          e.currentTarget.style.background = requiresSelection
            ? 'rgba(108,180,238,0.18)'
            : sb.surfaceSubtle;
        }}
        aria-haspopup={isClickable ? 'menu' : undefined}
        aria-expanded={isClickable ? open : undefined}
        aria-label={
          isClickable
            ? `Active business: ${labelText}. Click to switch.`
            : `Active business: ${labelText}`
        }
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: 'rgba(108,180,238,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            color: sb.accentBar,
            flexShrink: 0,
          }}
        >
          {labelText.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: requiresSelection ? '#FFFFFF' : sb.textActive,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {labelText}
          </div>
          {!requiresSelection && (
            <div
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: 'rgba(255,255,255,0.45)',
                marginTop: 1,
              }}
            >
              {memberships.length === 1 ? 'Active business' : 'Active · tap to switch'}
            </div>
          )}
        </div>
        {isClickable && (
          <ChevronDown
            size={14}
            color={sb.text}
            strokeWidth={2}
            style={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: `transform ${transition.fast}`,
              flexShrink: 0,
            }}
          />
        )}
      </button>

      {isClickable && open && (
        <BusinessPickerPopover
          memberships={memberships}
          activeBusinessId={activeBusinessId}
          onSelect={handleSelect}
          onClose={close}
        />
      )}
    </div>
  );
}
